'use client';

import clsx from 'clsx';
import { ReactNode } from 'react';

import styles from './SimpleTable.module.scss';

type Align = 'left' | 'center' | 'right';

export type SimpleTableColumn<T> = {
  header: ReactNode;
  key?: keyof T | string;
  render?: (row: T, index: number) => ReactNode;
  width?: number | string;
  align?: Align;
};

type SimpleTableProps<T> = {
  columns: Array<SimpleTableColumn<T>>;
  data: T[];
  getRowKey?: (row: T, index: number) => string;
  loading?: boolean;
  emptyMessage?: string;
  title?: ReactNode;
  dense?: boolean;
  className?: string;
};

export function SimpleTable<T extends Record<string, any>>({
  columns,
  data,
  getRowKey,
  loading = false,
  emptyMessage = 'No items to display.',
  title,
  dense = false,
  className,
}: SimpleTableProps<T>) {
  return (
    <div className={styles.tableWrapper}>
      {title ? <div className={styles.title}>{title}</div> : null}
      <table className={clsx(styles.table, dense && styles.dense, className)}>
        <thead className={styles.thead}>
          <tr>
            {columns.map((col, idx) => (
              <th
                key={idx}
                style={{ width: col.width, textAlign: col.align || 'left' }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={styles.tbody}>
          {loading ? (
            <tr>
              <td className={styles.cell} colSpan={columns.length}>
                Loading...
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td className={styles.cell} colSpan={columns.length}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, rIdx) => (
              <tr key={getRowKey ? getRowKey(row, rIdx) : rIdx.toString()}>
                {columns.map((col, cIdx) => {
                  const content = col.render
                    ? col.render(row, rIdx)
                    : col.key
                      ? (row as any)[col.key as string]
                      : null;
                  return (
                    <td
                      key={cIdx}
                      className={styles.cell}
                      style={{ textAlign: col.align || 'left' }}
                    >
                      {content}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
